import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiPlus,FiTrash2,FiChevronDown}=FiIcons;

const emptyGroup=()=>({
  name:'',
  required:false,
  multiple:false,
  values:[{label:'',price:0,pricing_mode:'per_item'}]
});

const normalizeOptions=(options)=>{
  if (!Array.isArray(options)) return [];
  return options.map((group)=>({
    name:group?.name || '',
    required:Boolean(group?.required),
    multiple:Boolean(group?.multiple),
    values:Array.isArray(group?.values) && group.values.length
      ? group.values.map((value)=>({
        label:value?.label || '',
        price:Number(value?.price) || 0,
        pricing_mode:value?.pricing_mode==='once_per_order'
          ? 'once_per_order'
          : 'per_item'
      }))
      : [{label:'',price:0,pricing_mode:'per_item'}]
  }));
};

const OfferingOptionsEditor=({
  value,
  onChange,
  templateGroups=[]
})=>{
  const groups=normalizeOptions(value);

  const updateGroup=(index,updates)=>{
    onChange(groups.map((group,groupIndex)=>(
      groupIndex===index ? {...group,...updates} : group
    )));
  };

  const updateValue=(groupIndex,valueIndex,updates)=>{
    onChange(groups.map((group,index)=>{
      if (index!==groupIndex) return group;

      return {
        ...group,
        values:group.values.map((value,itemIndex)=>(
          itemIndex===valueIndex ? {...value,...updates} : value
        ))
      };
    }));
  };

  const addGroup=()=>{
    onChange([...groups,emptyGroup()]);
  };

  const addTemplateGroups=()=>{
    const existingNames=new Set(
      groups.map((group)=>group.name.trim().toLowerCase())
    );

    const additions=templateGroups
      .filter((group)=>!existingNames.has(group.name.toLowerCase()))
      .map((group)=>({
        ...group,
        values:[{label:'',price:0,pricing_mode:'per_item'}]
      }));

    onChange([...groups,...additions]);
  };

  const removeGroup=(index)=>{
    onChange(groups.filter((_,groupIndex)=>groupIndex!==index));
  };

  const addValue=(groupIndex)=>{
    const group=groups[groupIndex];
    updateGroup(groupIndex,{
      values:[
        ...group.values,
        {label:'',price:0,pricing_mode:'per_item'}
      ]
    });
  };

  const removeValue=(groupIndex,valueIndex)=>{
    const group=groups[groupIndex];

    updateGroup(groupIndex,{
      values:group.values.filter((_,index)=>index!==valueIndex)
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-black uppercase tracking-widest text-slate-700">
            Custom Options
          </h4>
          <p className="mt-1 text-xs font-medium text-slate-400">
            Add category-specific choices and price adjustments.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {templateGroups.length>0 && (
            <button
              type="button"
              onClick={addTemplateGroups}
              className="rounded-xl bg-emerald-50 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-emerald-700"
            >
              Add Category Template
            </button>
          )}

          <button
            type="button"
            onClick={addGroup}
            className="flex items-center rounded-xl bg-indigo-50 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-indigo-600"
          >
            <SafeIcon icon={FiPlus} className="mr-2" />
            Add Group
          </button>
        </div>
      </div>

      {groups.length===0 && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-xs font-bold text-slate-400">
          No options added. Buyers will order the base item as listed.
        </div>
      )}

      {groups.map((group,groupIndex)=>(
        <div
          key={groupIndex}
          className="rounded-2xl border border-slate-100 bg-slate-50 p-5"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto_auto]">
            <input
              value={group.name}
              onChange={(event)=>updateGroup(groupIndex,{
                name:event.target.value
              })}
              className="rounded-xl border border-transparent bg-white px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Option group, e.g. Flavor"
            />

            <label className="flex items-center rounded-xl bg-white px-3 text-xs font-bold text-slate-600">
              <input
                type="checkbox"
                checked={group.required}
                onChange={(event)=>updateGroup(groupIndex,{
                  required:event.target.checked
                })}
                className="mr-2 text-indigo-600"
              />
              Required
            </label>

            <label className="flex items-center rounded-xl bg-white px-3 text-xs font-bold text-slate-600">
              <input
                type="checkbox"
                checked={group.multiple}
                onChange={(event)=>updateGroup(groupIndex,{
                  multiple:event.target.checked
                })}
                className="mr-2 text-indigo-600"
              />
              Multiple
            </label>

            <button
              type="button"
              onClick={()=>removeGroup(groupIndex)}
              className="flex items-center justify-center rounded-xl bg-white px-3 text-red-500"
              aria-label="Remove option group"
            >
              <SafeIcon icon={FiTrash2} />
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {group.values.map((optionValue,valueIndex)=>(
              <div
                key={valueIndex}
                className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_110px_150px_42px]"
              >
                <input
                  value={optionValue.label}
                  onChange={(event)=>updateValue(
                    groupIndex,
                    valueIndex,
                    {label:event.target.value}
                  )}
                  className="rounded-xl border border-transparent bg-white px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Choice, e.g. Chocolate"
                />

                <input
                  type="number"
                  step="0.01"
                  value={optionValue.price}
                  onChange={(event)=>updateValue(
                    groupIndex,
                    valueIndex,
                    {price:event.target.value}
                  )}
                  className="rounded-xl border border-transparent bg-white px-3 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="+0.00"
                />

                <select
                  value={optionValue.pricing_mode}
                  onChange={(event)=>updateValue(
                    groupIndex,
                    valueIndex,
                    {pricing_mode:event.target.value}
                  )}
                  className="rounded-xl border border-transparent bg-white px-3 py-3 text-xs font-bold text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500"
                  aria-label="Choice pricing frequency"
                >
                  <option value="per_item">Per item</option>
                  <option value="once_per_order">Once per order</option>
                </select>

                <button
                  type="button"
                  onClick={()=>removeValue(groupIndex,valueIndex)}
                  disabled={group.values.length===1}
                  className="rounded-xl bg-white text-red-400 disabled:opacity-30"
                  aria-label="Remove choice"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={()=>addValue(groupIndex)}
              className="flex items-center text-[10px] font-black uppercase tracking-widest text-indigo-600"
            >
              <SafeIcon icon={FiPlus} className="mr-2" />
              Add Choice
            </button>
          </div>

          <div className="mt-4 flex items-center text-[10px] font-bold text-slate-400">
            <SafeIcon icon={FiChevronDown} className="mr-2" />
            Enter positive prices for additions, or negative prices for discounts.
          </div>
        </div>
      ))}
    </div>
  );
};

export default OfferingOptionsEditor;